import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  Renderer2,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfigService, ScreenSize, ScreenSizeService } from '@libs/shared-services';
import {
  addGumCoordinates,
  asset,
  calculateCoordinates,
  CHARTING_CONTAINER_HEIGHT,
  CHARTING_TEETH_NUMBER_PADDING_Y,
  chartingViewBoxMatrix,
  CONTAINER_WIDTH,
  drawCross,
  drawGumGradientsByCoordinates,
  drawGumPeriosByCoordinates,
  drawSelectionBackgrounds,
  drawTeeth,
  drawTeethNumbers,
  GUM_INTERMEDIATE_BUMP,
  isReverse,
  Quadrant,
  Svg,
  SvgPath,
  TEETH_CONTAINER_PADDING_X,
  TEETH_CONTAINER_PADDING_Y,
  Tooth,
  TOOTH_FULL_HEIGHT,
  TOOTH_WIDTH,
  ToothWithLayers,
} from '@libs/ui-lib';
import { ChartBridge } from '@ffd/api-client';
import { select } from 'd3-selection';
import { GUM_INTERMEDIARY_BUMP, PERIO_TRANSLATE_Y, TEETH_TRANSLATE_Y } from './teeth.constants';

const QUADRANT_VERTICAL_PADDING = 30;

@Component({
  selector: 'lib-teeth',
  imports: [CommonModule],
  templateUrl: './teeth.component.html',
  styleUrl: './teeth.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeethComponent implements OnChanges, AfterViewInit {
  @ViewChild('chart', { static: true }) public chart: ElementRef | undefined;
  @ViewChild('selection', { static: true }) public selectionLayer: ElementRef | undefined;
  private _teeth: Tooth[] = [];

  @Input({ required: true })
  public set teeth(value: ToothWithLayers[]) {
    const currentHash = JSON.stringify(this._teeth);
    const newHash = JSON.stringify(value);

    if (currentHash !== newHash) {
      this._teeth = value;
      this.draw();
    }
  }
  public get teeth(): ToothWithLayers[] {
    return this._teeth;
  }

  constructor(protected readonly renderer: Renderer2) {}
  @Input() public bridges: ChartBridge[] = [];
  @Input() public drawGum = true;
  @Input() public drawCross = true;
  @Input() public drawTreatments = true;
  @Input() public drawNumbers = true;
  @Input() public selectedTeethPositions: number[] = [];
  @Input() public highlightedTeethPositions: number[] | null = null;
  @Input() public selectedQuadrant: Quadrant | null = null;
  @Output() public clicked = new EventEmitter<number>();
  @Output() public hovered = new EventEmitter<number>();

  private readonly screenSizeService = inject(ScreenSizeService);
  private readonly destroyRef = inject(DestroyRef);
  public readonly configService: ConfigService = inject(ConfigService);
  public teethAssetBasePath = this.configService.config()?.teethAssetBasePath ?? '';

  private screenSize: ScreenSize = 'xl';
  private svgSelectionBackground: Svg | null = null;
  private readonly chartingViewBox = signal<string | null>(null);

  private upperBuccalPathRef?: SvgPath;
  private readonly upperLingualPathRef?: SvgPath;
  private lowerLingualPathRef?: SvgPath;
  private lowerBuccalPathRef?: SvgPath;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['drawTreatments'] || changes['bridges']) {
      this.draw();
    }

    if (changes['highlightedTeethPositions'] || changes['selectedTeethPositions']) {
      this.drawSelectionBackgrounds();
    }

    if (changes['selectedQuadrant']) {
      this.handleQuadrantChange();
    }
  }

  public ngAfterViewInit(): void {
    this.handleQuadrantChange();

    this.screenSizeService.screenSize$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((screenSize) => {
      this.screenSize = screenSize;
      this.draw();
    });
  }

  private drawSelectionBackgrounds(): void {
    if (this.svgSelectionBackground) {
      this.svgSelectionBackground.selectAll('*').remove();

      const { upperJawBuccalTeethCoordinates, lowerJawBuccalTeethCoordinates } = calculateCoordinates(
        this.teeth,
        this.screenSize,
        TEETH_TRANSLATE_Y(this.screenSize),
        PERIO_TRANSLATE_Y(this.screenSize),
        GUM_INTERMEDIARY_BUMP(this.screenSize),
      );

      drawSelectionBackgrounds({
        svg: this.svgSelectionBackground,
        upperTeethCoordinates: upperJawBuccalTeethCoordinates,
        lowerTeethCoordinates: lowerJawBuccalTeethCoordinates,
        selectedTeeth: this.selectedTeethPositions,
        highlightedTeeth: this.highlightedTeethPositions,
        toothWidth: TOOTH_WIDTH(this.screenSize),
        toothHeight: TOOTH_FULL_HEIGHT(this.screenSize),
        containerHeight: CHARTING_CONTAINER_HEIGHT(this.screenSize),
      });
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private draw(): void {
    if (!this.chart || !this.selectionLayer || !this.teeth?.length) return;
    this.removeElements();

    // Use the custom viewBox if set (for zoom), otherwise use the default
    const viewBox = this.chartingViewBox() ?? chartingViewBoxMatrix[this.screenSize];

    /*    const wrapperSvg = select(this.chart.nativeElement)
      .append('svg')
      .attr('viewBox', viewBox)
      .style('width', '100%')
      .style('height', '100%');
    this.svgSelectionBackground = wrapperSvg.append('svg').style('width', '100%').style('height', '100%');*/
    this.svgSelectionBackground = select(this.selectionLayer.nativeElement)
      .append('svg')
      .attr('id', 'selection-background')
      .attr('viewBox', viewBox)
      .style('width', '100%')
      .style('height', '100%');

    const svg: Svg = select(this.chart.nativeElement)
      .append('svg')
      .attr('id', 'teeth')
      .attr('viewBox', viewBox)
      .style('width', '100%')
      .style('height', '100%');

    const {
      upperJawTeeth,
      lowerJawTeeth,
      upperJawBuccalTeethCoordinates,
      lowerJawBuccalTeethCoordinates,
      upperJawBuccalPeriosCoordinates,
      lowerJawBuccalPeriosCoordinates,
    } = calculateCoordinates(
      this.teeth,
      this.screenSize,
      TEETH_TRANSLATE_Y(this.screenSize),
      PERIO_TRANSLATE_Y(this.screenSize),
      GUM_INTERMEDIARY_BUMP(this.screenSize),
    );

    // 1. based on perios coordinates, draw gums
    if (this.drawGum) {
      const extendedUpperGumCoordinates = addGumCoordinates(
        upperJawBuccalPeriosCoordinates.map((t) => t.gingivalMargin),
        'upper',
        GUM_INTERMEDIATE_BUMP(this.screenSize),
        CONTAINER_WIDTH(this.screenSize),
        TEETH_CONTAINER_PADDING_Y(this.screenSize),
        TEETH_CONTAINER_PADDING_X(this.screenSize),
        upperJawBuccalTeethCoordinates[upperJawTeeth[0].position].y,
        TOOTH_FULL_HEIGHT(this.screenSize),
        0,
        'buccal',
      );
      const extendedLowerGumCoordinates = addGumCoordinates(
        lowerJawBuccalPeriosCoordinates.map((t) => t.gingivalMargin),
        'lower',
        GUM_INTERMEDIATE_BUMP(this.screenSize),
        CONTAINER_WIDTH(this.screenSize),
        TEETH_CONTAINER_PADDING_Y(this.screenSize),
        TEETH_CONTAINER_PADDING_X(this.screenSize),
        lowerJawBuccalTeethCoordinates[lowerJawTeeth[0].position].y,
        TOOTH_FULL_HEIGHT(this.screenSize),
        0,
        'buccal',
      );

      drawGumPeriosByCoordinates(svg, extendedUpperGumCoordinates);
      drawGumPeriosByCoordinates(svg, extendedLowerGumCoordinates);

      drawGumGradientsByCoordinates(
        svg,
        extendedUpperGumCoordinates.at(0)!,
        extendedLowerGumCoordinates.at(0)!,
        extendedUpperGumCoordinates.at(-1)!,
        extendedLowerGumCoordinates.at(-1)!,
      );
    }

    // 2. based on teeth coordinates, draw selection background and teeth
    this.drawSelectionBackgrounds();

    const toothAssetPath = (position: number) => `${asset(position)}/base/full.png`;
    const toothMaskPath = (position: number) => `${asset(position)}/mask.svg`;

    drawTeeth({
      svg,
      teeth: upperJawTeeth,
      bridges: this.bridges,
      teethCoordinates: upperJawBuccalTeethCoordinates,
      screenSize: this.screenSize,
      drawTreatments: this.drawTreatments,
      height: TOOTH_FULL_HEIGHT(this.screenSize),
      toothTransform: (position) => (isReverse(position) ? 'scale(-1, 1)' : 'scale(1)'),
      toothAssetPath,
      teethAssetBasePath: this.teethAssetBasePath,
      eventHandlers: {
        click: (position: number) => this.clicked.emit(position),
        hover: (position: number) => this.hovered.emit(position),
      },
      shouldDrawTooth: () => true,
      toothMaskPath,
    });
    drawTeeth({
      svg,
      teeth: lowerJawTeeth,
      bridges: this.bridges,
      teethCoordinates: lowerJawBuccalTeethCoordinates,
      screenSize: this.screenSize,
      drawTreatments: this.drawTreatments,
      height: TOOTH_FULL_HEIGHT(this.screenSize),
      toothTransform: (position) => (isReverse(position) ? 'scale(-1, 1)' : 'scale(1)'),
      toothAssetPath,
      teethAssetBasePath: this.teethAssetBasePath,
      eventHandlers: {
        click: (position: number) => this.clicked.emit(position),
        hover: (position: number) => this.hovered.emit(position),
      },
      shouldDrawTooth: () => true,
      toothMaskPath,
    });

    // 3. draw cross
    if (this.drawCross) {
      drawCross(svg, this.screenSize, CHARTING_CONTAINER_HEIGHT(this.screenSize));
    }

    // 5. draw teeth numbers
    if (this.drawNumbers) {
      drawTeethNumbers(
        svg,
        upperJawBuccalTeethCoordinates,
        'upper',
        this.screenSize,
        CHARTING_CONTAINER_HEIGHT(this.screenSize),
        CHARTING_TEETH_NUMBER_PADDING_Y(this.screenSize),
      );
      drawTeethNumbers(
        svg,
        lowerJawBuccalTeethCoordinates,
        'lower',
        this.screenSize,
        CHARTING_CONTAINER_HEIGHT(this.screenSize),
        CHARTING_TEETH_NUMBER_PADDING_Y(this.screenSize),
      );
    }
  }

  private handleQuadrantChange(): void {
    const quadrant = this.selectedQuadrant;
    if (quadrant) {
      switch (quadrant) {
        case Quadrant.TopLeft: {
          this.zoomToTopRight();
          break;
        }
        case Quadrant.TopRight: {
          this.zoomToTopLeft();
          break;
        }
        case Quadrant.BottomLeft: {
          this.zoomToBottomRight();
          break;
        }
        case Quadrant.BottomRight: {
          this.zoomToBottomLeft();
          break;
        }
      }
    } else {
      this.zoomToCenter();
    }
  }

  // Zoom into the top left quadrant
  public zoomToTopLeft(): void {
    const containerHeight = CHARTING_CONTAINER_HEIGHT(this.screenSize);
    const containerWidth = CONTAINER_WIDTH(this.screenSize);
    this.zoomToViewBox(`0 -${QUADRANT_VERTICAL_PADDING} ${containerWidth / 2} ${containerHeight / 2 + QUADRANT_VERTICAL_PADDING}`);
  }

  // Zoom into the top right quadrant
  public zoomToTopRight(): void {
    const containerHeight = CHARTING_CONTAINER_HEIGHT(this.screenSize);
    const containerWidth = CONTAINER_WIDTH(this.screenSize);
    this.zoomToViewBox(
      `${containerWidth / 2} -${QUADRANT_VERTICAL_PADDING} ${containerWidth / 2} ${containerHeight / 2 + QUADRANT_VERTICAL_PADDING}`,
    );
  }

  // Zoom into the bottom left corner
  public zoomToBottomLeft(): void {
    const containerHeight = CHARTING_CONTAINER_HEIGHT(this.screenSize);
    const containerWidth = CONTAINER_WIDTH(this.screenSize);
    this.zoomToViewBox(`0 ${containerHeight / 2} ${containerWidth / 2} ${containerHeight / 2 + QUADRANT_VERTICAL_PADDING}`);
  }

  // Zoom into the bottom right corner
  public zoomToBottomRight(): void {
    const containerHeight = CHARTING_CONTAINER_HEIGHT(this.screenSize);
    const containerWidth = CONTAINER_WIDTH(this.screenSize);
    this.zoomToViewBox(
      `${containerWidth / 2} ${containerHeight / 2} ${containerWidth / 2} ${containerHeight / 2 + QUADRANT_VERTICAL_PADDING}`,
    );
  }

  // Zoom back to center
  public zoomToCenter(): void {
    this.zoomToViewBox(chartingViewBoxMatrix[this.screenSize]);
  }

  // Helper method to change the viewBox without animation
  private zoomToViewBox(viewBox: string): void {
    this.chartingViewBox.set(viewBox);
    const svg = select(this.chart?.nativeElement).select('svg');
    svg.attr('viewBox', viewBox);
  }

  private removeElements(): void {
    if (!this.chart || !this.selectionLayer) return;
    this.upperBuccalPathRef?.remove();
    this.lowerLingualPathRef?.remove();
    this.upperBuccalPathRef?.remove();
    this.lowerBuccalPathRef?.remove();
    this.upperBuccalPathRef = undefined;
    this.lowerLingualPathRef = undefined;
    this.upperBuccalPathRef = undefined;
    this.lowerBuccalPathRef = undefined;

    select(this.chart.nativeElement).select('svg').remove();
    select(this.selectionLayer.nativeElement).select('svg').remove();
  }
}
